import React, { useState, useEffect } from 'react';
import { superadminApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Loader2, Save, RefreshCw, BellRing, ArrowUpCircle } from 'lucide-react';
import { format } from 'date-fns';

const fmt = (iso) => {
  if (!iso) return 'Not run yet';
  try { return format(new Date(iso), 'd MMM yyyy, HH:mm'); } catch { return iso; }
};

// Hours are the storage unit (the thresholds are compared against elapsed hours),
// but "168 hours" means nothing to a human — show days alongside.
const inDays = (h) => {
  const n = Number(h);
  if (!n || Number.isNaN(n)) return '';
  const d = n / 24;
  if (d < 1) return `${n} hour${n === 1 ? '' : 's'}`;
  return `${Number.isInteger(d) ? d : d.toFixed(1)} day${d === 1 ? '' : 's'}`;
};

const ApprovalRemindersManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  // The employee's own one-shot chase. A SEPARATE switch from `enabled`: "customers
  // complain their staff chase directors" and "requests rot unanswered" are different
  // problems, and turning the ladder off to fix the first would cause the second.
  const [nudgeEnabled, setNudgeEnabled] = useState(true);
  const [hours, setHours] = useState(['48', '96', '120']);
  const [escalate, setEscalate] = useState('168');
  const [status, setStatus] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await superadminApi.getApprovalReminders();
      setEnabled(!!data.enabled);
      setNudgeEnabled(data.nudge_enabled !== false);
      setHours((data.reminder_hours || []).map(String));
      setEscalate(String(data.escalate_hours ?? '168'));
      setStatus(data.status || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the reminder settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setHour = (i, v) => setHours((prev) => prev.map((h, idx) => (idx === i ? v : h)));

  // Mirrors the server-side rule. Checking here too means the reason appears next to
  // the field rather than as a toast after a round trip.
  const nums = hours.map((h) => parseInt(h, 10)).filter((n) => !Number.isNaN(n));
  const escNum = parseInt(escalate, 10);
  const outOfOrder = nums.length > 1 && nums.some((n, i) => i > 0 && n <= nums[i - 1]);
  const escalateTooEarly = nums.length > 0 && !Number.isNaN(escNum) && escNum <= Math.max(...nums);
  const invalid = outOfOrder || escalateTooEarly || Number.isNaN(escNum);

  const save = async () => {
    if (invalid) return;
    setSaving(true);
    try {
      const { data } = await superadminApi.updateApprovalReminders({
        enabled,
        nudge_enabled: nudgeEnabled,
        reminder_hours: nums,
        escalate_hours: escNum,
      });
      setHours((data.reminder_hours || []).map(String));
      setEscalate(String(data.escalate_hours));
      toast.success('Reminder settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the reminder settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground p-6">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              <BellRing className="w-4 h-4" /> Chase unanswered leave requests
            </h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              When a request is waiting on someone, we nudge them up to three times and
              then hand it to the company's admins. The clock restarts at each approval
              step, so a two-step flow doesn't chase the second person while the first
              is still deciding. These apply to every customer — they can't change them.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Label htmlFor="ar-enabled" className="text-sm">{enabled ? 'On' : 'Off'}</Label>
            <Switch id="ar-enabled" checked={enabled} onCheckedChange={setEnabled} />
          </div>
        </div>

        <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 ${enabled ? '' : 'opacity-50 pointer-events-none'}`}>
          {[0, 1, 2].map((i) => (
            <div key={i}>
              <Label className="text-xs">
                {i === 2 ? 'Final reminder' : `Reminder ${i + 1}`}
              </Label>
              <Input
                className="input-hive mt-1"
                type="number"
                min={1}
                max={720}
                value={hours[i] ?? ''}
                onChange={(e) => setHour(i, e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground mt-1">{inDays(hours[i])} after it lands</p>
            </div>
          ))}
          <div>
            <Label className="text-xs flex items-center gap-1">
              <ArrowUpCircle className="w-3 h-3" /> Hand to admins
            </Label>
            <Input
              className="input-hive mt-1"
              type="number"
              min={1}
              max={720}
              value={escalate}
              onChange={(e) => setEscalate(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground mt-1">{inDays(escalate)} after it lands</p>
          </div>
        </div>

        {outOfOrder && (
          <p className="text-xs text-amber-600 dark:text-amber-500 mt-3">
            Reminders need to go in order — each one later than the last.
          </p>
        )}
        {escalateTooEarly && (
          <p className="text-xs text-amber-600 dark:text-amber-500 mt-3">
            Handing to admins has to come after the final reminder, or the reminders
            never get sent.
          </p>
        )}

        <p className="text-xs text-muted-foreground mt-4">
          Nothing is ever approved automatically — a request that reaches the admins is
          still pending and still needs a decision. The wording of each email lives in{' '}
          <strong>Email Templates</strong>.
        </p>

        {/* The employee's own chase, deliberately its own switch — see the note on
            `nudgeEnabled` above. Kept on this screen because it's the same question
            ("who gets chased about an unanswered request, and when"). */}
        <div className="mt-5 pt-5 border-t border-border flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-xl">
            <p className="text-sm font-medium">Let people chase their own request</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Someone waiting on a decision can send <strong>one</strong> reminder per
              request, and only after it has been waiting a day — before that the button
              says when it becomes available. The email is worded as a system notice, not
              a personal chase, and the screen tells them when the automatic reminders
              above are due so they can decide it isn't worth using. Turn this off if
              customers tell you staff are hounding their approvers; the automatic
              reminders above carry on either way.
            </p>
          </div>
          <div className="flex items-center gap-3 whitespace-nowrap">
            <Label htmlFor="ar-nudge" className="text-sm">{nudgeEnabled ? 'On' : 'Off'}</Label>
            <Switch id="ar-nudge" checked={nudgeEnabled} onCheckedChange={setNudgeEnabled} />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <Button onClick={save} disabled={saving || invalid}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save settings
          </Button>
          <Button variant="outline" onClick={load} disabled={saving}>
            <RefreshCw className="w-4 h-4 mr-2" /> Reload
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="font-semibold">Last check</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Runs every hour, and once on startup.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
          <div>
            <div className="text-muted-foreground text-xs">Last run</div>
            <div className="font-medium">{fmt(status?.last_run_at)}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Reminders sent</div>
            <div className="font-medium">{status?.reminders_sent ?? 0}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Handed to admins</div>
            <div className="font-medium">{status?.escalations ?? 0}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Errors</div>
            <div className={`font-medium ${status?.errors ? 'text-red-600' : ''}`}>{status?.errors ?? 0}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalRemindersManager;
